import { devbackendUrl } from './constants';

// Generate a unique visitor ID (stored in localStorage)
const getVisitorId = (): string => {
  let visitorId = localStorage.getItem('kokki_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('kokki_visitor_id', visitorId);
  }
  return visitorId;
};

// Generate a session ID (stored in sessionStorage)
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('kokki_session_id');
  if (!sessionId) {
    sessionId = 's_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    sessionStorage.setItem('kokki_session_id', sessionId);
  }
  return sessionId;
};

// Detect device type
const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Detect browser
const getBrowser = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('SamsungBrowser')) return 'Samsung Browser';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  if (ua.includes('Trident')) return 'IE';
  if (ua.includes('Edge')) return 'Edge Legacy';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  return 'Unknown';
};

// Detect OS
const getOS = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac OS')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Unknown';
};

// Get connection type (if available)
const getConnectionType = (): string | undefined => {
  const nav = navigator as any;
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
  return conn?.effectiveType || undefined;
};

// Get device capabilities
const getDeviceCapabilities = () => ({
  viewportWidth: window.innerWidth,
  viewportHeight: window.innerHeight,
  colorDepth: window.screen.colorDepth,
  touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
  connectionType: getConnectionType(),
  cpuCores: navigator.hardwareConcurrency || undefined,
  deviceMemory: (navigator as any).deviceMemory || undefined,
});

// Parse UTM parameters from URL
const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get('utm_source') || undefined,
    utmMedium: params.get('utm_medium') || undefined,
    utmCampaign: params.get('utm_campaign') || undefined,
  };
};

// Analytics API calls
const analyticsApi = {
  async initSession() {
    const utm = getUTMParams();
    const capabilities = getDeviceCapabilities();
    try {
      const response = await fetch(`${devbackendUrl}analytics/init-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitorId: getVisitorId(),
          sessionId: getSessionId(),
          referrer: document.referrer || undefined,
          entryPage: window.location.pathname,
          deviceType: getDeviceType(),
          browser: getBrowser(),
          os: getOS(),
          screenWidth: window.screen.width,
          screenHeight: window.screen.height,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          language: navigator.language,
          ...capabilities,
          ...utm,
        }),
      });
      return response.json();
    } catch (error) {
      console.debug('[Analytics] Init session failed:', error);
    }
  },

  async trackPageView(path: string, title?: string) {
    try {
      await fetch(`${devbackendUrl}analytics/page-view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitorId: getVisitorId(),
          sessionId: getSessionId(),
          path,
          title: title || document.title,
        }),
      });
    } catch (error) {
      console.debug('[Analytics] Track page view failed:', error);
    }
  },

  async trackEvent(eventName: string, eventCategory?: string, eventData?: Record<string, unknown>) {
    try {
      await fetch(`${devbackendUrl}analytics/event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitorId: getVisitorId(),
          sessionId: getSessionId(),
          eventName,
          eventCategory,
          eventData,
          page: window.location.pathname,
        }),
      });
    } catch (error) {
      console.debug('[Analytics] Track event failed:', error);
    }
  },

  async endSession() {
    try {
      await fetch(`${devbackendUrl}analytics/end-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: getSessionId(),
          exitPage: window.location.pathname,
        }),
      });
    } catch (error) {
      console.debug('[Analytics] End session failed:', error);
    }
  },

  async linkUser(userId: number) {
    try {
      await fetch(`${devbackendUrl}analytics/link-user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitorId: getVisitorId(),
          sessionId: getSessionId(), // Include current session for linking
          userId,
        }),
      });
    } catch (error) {
      console.debug('[Analytics] Link user failed:', error);
    }
  },
};

export { analyticsApi, getVisitorId, getSessionId };

